import React, { useState, useEffect, useRef } from "react";
import { SketchPicker } from "react-color";
import { TextField, IconButton, Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button } from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import { useFetchMasterPriorityStatusQuery } from "../../../../slice/logdetailSlice";
import { deleteCommentEntities, postdatas,postResource } from "../../../../apis/apiService";
import Loader from "../../../../components/common/Loader";
import { toast } from "react-toastify";

const PriorityIssue = () => {
  const { data, refetch, isLoading } = useFetchMasterPriorityStatusQuery();
  const [fields, setFields] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [showColorPicker, setShowColorPicker] = useState(null);
  const [statusType, setStatusType] = useState("");
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    field: null,
  });
  const colorPickerRef = useRef(null);

  useEffect(() => {
    if (data) {
      const taskStatus = data.find((list) => list?.priority_type === "ISSUE");
      // //console.log()("task_priority_name priorityIssue: ", data);

      if (taskStatus) {
        setStatusType(taskStatus?.priority_type);
        setFields(
          taskStatus?.priority_details.map((status) => ({
            id: status.priority_id,
            priority_name: status.priority_name,
            priority_colour: status.priority_colour,
            isNew: false, // Existing fields are not new
            isEditing: false, // Edit mode initially off
          }))
        );
      }
    }
  }, [data]);

  // API Update function
  const handleUpdate = async (updatedField) => {
    if (updatedField.isNew) return; // Skip API call for new fields

    try {
      // //console.log()("Updating_field_id :", updatedField);
      const requestedData = {
        priority_name: updatedField.priority_name,
        priority_colour: updatedField.priority_colour,
      };
      const response = await postResource( "master/priority", "update", updatedField.id, requestedData );
      toast.success(response.message);
      setFields((prevFields) =>
        prevFields.map((f) =>
          f.id === updatedField.id ? { ...f, isEditing: false } : f
        )
      );
      setShowColorPicker(null);
    } 
    catch (err) {
      console.error("Error updating status:", err.message);
      toast.error(err.message || err.response.data.message);
    }
  };

  // Edit text
  const handleEditLabel = (id, value) => {
    setFields((prevFields) =>
      prevFields.map((f) => (f.id === id ? { ...f, priority_name: value } : f))
    );
  };

  // Add new field
  const handleAddField = () => {
    const newField = {
      id: Date.now(),
      priority_name: "New Status",
      priority_colour: "#808080",
      isNew: true, // Mark as new
    };
    setFields([...fields, newField]);
  };

  // Change color
  const handleChangeColor = (id, color) => {
    setFields((prevFields) =>
      prevFields.map((f) =>
        f.id === id ? { ...f, priority_colour: color.hex } : f
      )
    );
  };

  // Save new field
  const handleSaveNewField = async (id) => {
    const savedField = fields.find((f) => f.id === id);
    if (!savedField) return;

    // //console.log()("New_field_saved:", savedField.priority_colour);

    // Convert to normal field (remove "isNew")
    setFields((prevFields) =>
      prevFields.map((f) => (f.id === id ? { ...f, isNew: false } : f))
    );
    const requestedData = {
      priority_name: savedField.priority_name,
      priority_colour: savedField.priority_colour,
    };

    try {
      const response = await postdatas( "master/priority/create", "ISSUE", requestedData );
      toast.success(response.message);
      // // //console.log()("New_field_saved api response :", response);
      // if (response) {
      refetch();
      // }
    } 
    catch (err) {
      console.error("Error saving new status:", err.message);
      toast.error(err.message || err.response.data.message);
    }
  };

  // Enable editing on clicking edit button
  const handleEditClick = (id) => {
    setFields((prevFields) =>
      prevFields.map((f) => (f.id === id ? { ...f, isEditing: true } : f))
    );
  };

  // Save on blur (Update API only for existing fields)
  const handleBlur = (id) => {
    const updatedField = fields.find((f) => f.id === id);
    if (updatedField && !updatedField.isNew) {
      handleUpdate(updatedField);
    }
  };

  // Open delete confirmation popup
  const handleDeleteClick = (field) => {
    setDeleteDialog({ open: true, field });
  };

  // Delete API call
  const handleDelete = async () => {
    if (!deleteDialog.field) return;
    try {
      const response = await deleteCommentEntities("master/priority/delete", deleteDialog.field.id);
      toast.success(response.message);
      setFields((prevFields) =>
        prevFields.filter((f) => f.id !== deleteDialog.field.id)
      );
    } 
    catch (err) {
      console.error("Error deleting field:", err.message);
      toast.error(err.message || err.response.data.message);
    } 
    finally {
      setDeleteDialog({ open: false, field: null });
    }
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        colorPickerRef.current &&
        !colorPickerRef.current.contains(event.target)
      ) {
        if (showColorPicker !== null) {
          const updatedField = fields.find((f) => f.id === showColorPicker);
          if (updatedField) {
            handleUpdate(updatedField); // Call API only after color picker closes
          }
        }
        setShowColorPicker(null);
      }
    }

    if (showColorPicker !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showColorPicker, fields]);

  return (
    <Box sx={{ display: "grid", gridColumn: 2 }}>
      {isLoading ? (
        <Loader />
      ) : (
        <Box p={3}>
          <h4>Issue</h4>
          <span className="line"></span>
          <p className=" text-secondary">
            Click the edit icon to edit text, or click the color box to change
            color.
          </p>

          <form>
            {fields.map((field) => (
              <Box key={field.id} display="flex" alignItems="center" mb={2}>
                {/* Color Picker */}
                <Box
                  width={32}
                  height={33}
                  bgcolor={field.priority_colour}
                  borderRadius={1}
                  onClick={() => setShowColorPicker(field.id)}
                  sx={{ cursor: "pointer", border: "1px solid #ccc" }}
                />
                <Box ml={1}>
                  <TextField
                    fullWidth
                    value={field.priority_name}
                    onChange={(e) => handleEditLabel(field.id, e.target.value)}
                    onBlur={() => handleBlur(field.id)}
                    autoFocus={field.isNew || field.isEditing} // Auto-focus only for new or edited fields
                    disabled={!field.isNew && !field.isEditing} // Disable unless it's new or in edit mode
                  />
                </Box>

                {/* Buttons */}
                <Box display="flex" gap={1}>
                  {field.isNew ? (
                    <button
                      className=" mx-3 add_btn"
                      onClick={() => handleSaveNewField(field.id)}
                    >
                      save
                    </button>
                  ) : (
                    <>
                      <IconButton onClick={() => handleEditClick(field.id)}>
                        <Edit color="primary" />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteClick(field)}>
                        <Delete color="error" />
                      </IconButton>
                    </>
                  )}
                </Box>

                {/* Color Picker Popup */}
                {showColorPicker === field.id && (
                  <div
                    ref={colorPickerRef}
                    style={{ position: "absolute", zIndex: 2 }}
                  >
                    <SketchPicker
                      color={field.priority_colour}
                      onChange={(color) => handleChangeColor(field.id, color)}
                    />
                  </div>
                )}
              </Box>
            ))}
            
            <div className="d-flex gap-3 align-items-center">
              <button
                type="button"
                className="addmore_btn"
                onClick={handleAddField}
              >
                + Add Field
              </button>
            </div>
          </form>

          {/* Delete confirmation dialog */}
          <Dialog
            open={deleteDialog.open}
            onClose={() => setDeleteDialog({ open: false, field: null })}
          >
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to delete the field "
                {deleteDialog.field?.priority_name}"?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setDeleteDialog({ open: false, field: null })}
              >
                No
              </Button>
              <Button onClick={handleDelete} color="error">
                Yes
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}
    </Box>
  );
};

export default PriorityIssue;
